import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LinkInvite.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
export const PENDING_SHARE_INVITE_KEY = 'pendingShareInvite';

interface InviteInfo {
  status: 'valid' | 'accepted' | 'expired' | 'revoked' | 'invalid';
  inviteEmail?: string;
  projectName?: string;
  ownerName?: string;
  expiresAt?: string | null;
}

const UNAVAILABLE: Record<string, string> = {
  expired: 'This invite has expired. Ask the sender for a new one.',
  revoked: 'This invite was withdrawn by the sender.',
  invalid: 'This invite link is not valid. Ask the sender for a new one.',
};

const ShareInvite: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [acceptedProjectId, setAcceptedProjectId] = useState<string | null>(null);

  useEffect(() => {
    // Back from sign-in (see ProtectedRoute): the stored token has done its job.
    try { localStorage.removeItem(PENDING_SHARE_INVITE_KEY); } catch { /* storage unavailable */ }
    let cancelled = false;
    fetch(`${API_BASE}/api/share-invites/${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then((data: InviteInfo) => { if (!cancelled) setInfo(data); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [token]);

  const goToLogin = () => {
    try { localStorage.setItem(PENDING_SHARE_INVITE_KEY, token); } catch { /* storage unavailable */ }
    window.location.href = '/login';
  };

  const accept = async () => {
    setAccepting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/share-invites/${encodeURIComponent(token)}/accept`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not accept the invite');
      setAcceptedProjectId(data.projectId);
    } catch (e: any) {
      setError(e.message || 'Could not accept the invite');
    } finally {
      setAccepting(false);
    }
  };

  if (acceptedProjectId) return <Navigate to="/projects" replace />;

  let body: React.ReactNode;
  if (loadFailed) {
    body = <p className="li-text">Could not load this invite. Please try again.</p>;
  } else if (!info || isLoading) {
    body = <p className="li-text">Loading…</p>;
  } else if (info.status !== 'valid' && info.status !== 'accepted') {
    body = (
      <>
        <h1 className="li-title">Invite unavailable</h1>
        <p className="li-text">{UNAVAILABLE[info.status]}</p>
      </>
    );
  } else {
    const wrongAccount = isAuthenticated && user?.email && info.inviteEmail
      && user.email.toLowerCase() !== info.inviteEmail.toLowerCase();
    body = (
      <>
        <h1 className="li-title">{info.ownerName} shared a project with you</h1>
        <p className="li-text">
          <strong>{info.projectName}</strong> — read-only access
          {info.expiresAt ? ` until ${new Date(info.expiresAt).toLocaleDateString()}` : ''}.
        </p>
        <p className="li-note">Invited email: {info.inviteEmail}</p>
        {error && <p className="li-note" role="alert">{error}</p>}
        {!isAuthenticated ? (
          <>
            <button type="button" className="li-btn" onClick={goToLogin}>Sign in or sign up to accept</button>
            <p className="li-note">Use the Google account for {info.inviteEmail}.</p>
          </>
        ) : wrongAccount ? (
          <p className="li-note">You are signed in as {user?.email}. Sign in with {info.inviteEmail} to accept.</p>
        ) : (
          <button type="button" className="li-btn" onClick={accept} disabled={accepting}>
            {accepting ? 'Accepting…' : 'Accept invite'}
          </button>
        )}
        <p className="li-note"><Link to="/projects">Go to my projects</Link></p>
      </>
    );
  }

  return (
    <div className="li-page">
      <div className="li-card">{body}</div>
    </div>
  );
};

export default ShareInvite;
